/**
 * Merge persisted block.config.section.options with live field.definition.options.
 *
 * For select/enum sectioning, persisted options are an *order overlay* only:
 * - keep survivor values in persisted order
 * - drop values removed from the definition
 * - append new definition values after survivors
 * - prefer live definition labels/colors
 *
 * When there is no definition option list, returns the persisted snapshot as-is
 * (used for relation section pickers that store chosen instance ids).
 */

export type SectionOptionValue = {
  value: string
  label: string
  color?: string | null
  icon?: string | null
}

export function mergeSectionOptionsWithDefinition(args: {
  persistedOptions?: Array<{ value: string; label: string }> | null
  definitionOptions?: Array<{
    value: string
    label: string
    color?: string | null
    icon?: string | null
  }> | null
}): SectionOptionValue[] {
  const persisted = Array.isArray(args.persistedOptions)
    ? args.persistedOptions
    : []
  const definition = Array.isArray(args.definitionOptions)
    ? args.definitionOptions
    : []

  if (definition.length === 0) {
    return persisted.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }))
  }

  const definitionByValue = new Map(
    definition.map((opt) => [opt.value, opt] as const),
  )
  const seen = new Set<string>()
  const merged: SectionOptionValue[] = []

  for (const opt of persisted) {
    const live = definitionByValue.get(opt.value)
    if (!live || seen.has(opt.value)) continue
    seen.add(opt.value)
    merged.push({
      value: live.value,
      label: live.label,
      color: live.color ?? null,
      ...(live.icon ? { icon: live.icon } : {}),
    })
  }

  for (const opt of definition) {
    if (seen.has(opt.value)) continue
    seen.add(opt.value)
    merged.push({
      value: opt.value,
      label: opt.label,
      color: opt.color ?? null,
      ...(opt.icon ? { icon: opt.icon } : {}),
    })
  }

  return merged
}
